import type Keycloak from 'keycloak-js';
import type { TSessionData } from '~/models/Session';

import authService from '~/services/authService';
import { ParsedToken } from '~/models/ParsedToken';
import { Profile } from '~models/Profile';
import { SESSION_STATUS_ENUM } from '~/enums/Session.enum';
import { Session } from '~/models/Session';
import { USER_PROFILE_TYPE_ENUM } from '~/enums/Users.enum';
import { defineStore } from 'pinia';
import { jwtDecode } from 'jwt-decode';
import { useCompanyStore } from './useCompanyStore';
import { useEventStore } from './useEventStore';
import { useProfileStore } from './useProfileStore';
import FeatureFlagService from '~/services/FeatureFlagService';

export type TAuthStore = ReturnType<typeof useAuthStore>;
type SessionInfo = {
  token: string;
  refreshToken: string;
  authenticated: boolean;
};

export const useAuthStore = defineStore(
  'MY_SCAN_SOURCE_AUTH_STORE',
  () => {
    const LAYOUT_STORE = useLayoutStore();
    const COMPANY_STORE = useCompanyStore();
    const PROFILE_STORE = useProfileStore();
    const EVENT_STORE = useEventStore();

    const session = ref<Session>(new Session());
    const authenticated = computed(() => authService.CallIsAuthenticated());
    const resellerDocument = computed(() => session.value.currentEconomicGroupMember);

    window.addEventListener('beforeunload', () => {
      session.value.interruptSession(SESSION_STATUS_ENUM.WINDOW_CLOSED);
      EVENT_STORE.process('OnSessionClose');
    });

    function onUpdateOauth(keycloak: Keycloak): void {
      const { token, refreshToken, authenticated } = keycloak;
      if (!token || !refreshToken || !authenticated) return;

      FeatureFlagService.CallInit();

      session.value.refreshSession(adaptSessionData({ token, refreshToken, authenticated }));

      COMPANY_STORE.dispatchGetEconomicGroupMembers(session.value.economicGroupMembers);
      PROFILE_STORE.setProfileData(
        new Profile(
          session.value.name,
          session.value.lastName,
          session.value.groups,
          session.value.email,
        ),
      );
    }

    function onLogout() {
      LAYOUT_STORE.hideEconomicGroupModal();
      authService.CallLogout();
      session.value.interruptSession(SESSION_STATUS_ENUM.LOGGED_OUT);
      EVENT_STORE.process('OnSessionClose');
    }

    function logout(is500 = false, is403 = false): void | Session {
      LAYOUT_STORE.hideEconomicGroupModal();
      if (is403) return session.value.interruptSession(SESSION_STATUS_ENUM.NOT_ALLOWED);
      if (is500) return session.value.interruptSession(SESSION_STATUS_ENUM.UNAVAILABLE);
      authService.CallLogout();
      session.value.interruptSession(SESSION_STATUS_ENUM.LOGGED_OUT);
      EVENT_STORE.process('OnSessionClose');
    }

    async function login(): Promise<void> {
      await authService.CallLogin();
    }

    async function refreshToken(): Promise<void> {
      await authService.CallTokenRefresh();
    }

    async function isExpired(): Promise<boolean> {
      return await authService.CallIsTokenExpired();
    }

    function adaptSessionData(sessionInfo: SessionInfo): TSessionData {
      const { token, refreshToken, authenticated } = sessionInfo;
      const parsedToken = new ParsedToken(jwtDecode(token));

      // Determine currentEconomicGroupMember based on context:
      // 1. If it's a new user login (different email), use token's tenant
      // 2. If it's the same user (page reload/refresh), keep current selection
      let currentEconomicGroupMember = parsedToken.tenantId?.[0] || ''; // Default to token's tenant

      if (session.value.email && session.value.email === parsedToken.email) {
        // Same user - keep current selection if it exists and is valid
        if (
          session.value.currentEconomicGroupMember &&
          parsedToken.tenantId?.includes(session.value.currentEconomicGroupMember)
        ) {
          currentEconomicGroupMember = session.value.currentEconomicGroupMember;
        }
      }

      const sessionData: TSessionData = {
        token,
        refreshToken,
        authenticated: !!authenticated,
        sessionStatus: authenticated ? SESSION_STATUS_ENUM.DEFAULT : SESSION_STATUS_ENUM.FORBIDDEN,
        economicGroupMembers: parsedToken.tenantId || [],
        currentEconomicGroupMember,
        name: parsedToken.givenName,
        lastName: parsedToken.familyName,
        userName: parsedToken.preferredUsername,
        email: parsedToken.email,
        roles: parsedToken.roles || [],
        groups: (parsedToken.groups || []).map((group: string) => {
          const key =
            USER_PROFILE_TYPE_ENUM_MAPPER[group as keyof typeof USER_PROFILE_TYPE_ENUM_MAPPER];

          return USER_PROFILE_TYPE_ENUM[key];
        }),
      };

      return sessionData;
    }

    return {
      authenticated,
      session,
      resellerDocument,
      isExpired,
      onUpdateOauth,
      logout,
      onLogout,
      login,
      refreshToken,
    };
  },
  { persist: true },
);
